import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, useCart } from "@/App";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Copy } from "lucide-react";

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { cart, refreshCart } = useCart();
  const [paymentMethod, setPaymentMethod] = useState("");
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [shipping, setShipping] = useState({
    address: "",
    city: "",
    state: "",
    zip: "",
    phone: ""
  });

  const paymentMethods = [
    { id: "zelle", name: "Zelle", handle: "gripcityracing@gmail.com", color: "#6D1ED4" },
    { id: "cashapp", name: "Cash App", handle: "$GripCityRacing", color: "#00D632" },
    { id: "venmo", name: "Venmo", handle: "@GripCityRacing", color: "#008CFF" }
  ];

  const copyHandle = (handle) => {
    navigator.clipboard.writeText(handle);
    toast.success("Copied to clipboard!");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentMethod) {
      toast.error("Please select a payment method");
      return;
    }

    if (!shipping.address || !shipping.city || !shipping.state || !shipping.zip || !shipping.phone) {
      toast.error("Please fill in all shipping details");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/orders`,
        {
          payment_method: paymentMethod,
          shipping_address: shipping.address,
          shipping_city: shipping.city,
          shipping_state: shipping.state,
          shipping_zip: shipping.zip,
          phone: shipping.phone
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrderId(response.data.id);
      setOrderComplete(true);
      await refreshCart();
      toast.success("Order placed successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to place order");
    } finally {
      setLoading(false);
    }
  };

  const shippingCost = cart.total >= 100 ? 0 : 9.99;
  const totalWithShipping = cart.total + shippingCost;

  if (orderComplete) {
    return (
      <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8" data-testid="order-complete">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#39FF14]/10 flex items-center justify-center">
            <CheckCircle size={48} className="text-[#39FF14]" />
          </div>
          <h1 className="text-4xl heading text-[#EDEDED] mb-4">Order Confirmed!</h1>
          <p className="text-[#888] mb-2">Order ID: <span className="text-[#39FF14]">{orderId}</span></p>
          <p className="text-[#888] mb-8">
            Thank you for your order. Please complete your payment using the method you selected.
          </p>

          <div className="bg-[#0A0A0A] border border-[#222] p-8 text-left mb-8">
            <h3 className="text-xl heading text-[#EDEDED] mb-4">Payment Instructions</h3>
            {paymentMethods.find(m => m.id === paymentMethod) && (
              <div>
                <p className="text-[#888] mb-4">
                  Send <span className="text-[#39FF14] font-bold">${totalWithShipping.toFixed(2)}</span> to:
                </p>
                <div className="flex items-center gap-4 p-4 border border-[#333] bg-black/30">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: paymentMethods.find(m => m.id === paymentMethod)?.color }}
                  >
                    {paymentMethod === "zelle" ? "Z" : paymentMethod === "cashapp" ? "$" : "V"}
                  </div>
                  <div className="flex-1">
                    <p className="text-[#EDEDED] font-semibold">
                      {paymentMethods.find(m => m.id === paymentMethod)?.name}
                    </p>
                    <p className="text-[#39FF14] label-text">
                      {paymentMethods.find(m => m.id === paymentMethod)?.handle}
                    </p>
                  </div>
                  <button
                    onClick={() => copyHandle(paymentMethods.find(m => m.id === paymentMethod)?.handle)}
                    className="p-2 text-[#888] hover:text-[#39FF14] transition-colors"
                  >
                    <Copy size={20} />
                  </button>
                </div>
                <p className="mt-4 text-[#888] text-sm">
                  Include your Order ID <span className="text-[#39FF14]">{orderId?.slice(0, 8)}</span> in the payment note.
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate("/profile")}
              className="btn-skew bg-[#39FF14] text-black px-8 py-4 font-bold uppercase"
              data-testid="view-orders-btn"
            >
              <span>View Orders</span>
            </button>
            <button
              onClick={() => navigate("/shop")}
              className="btn-skew border border-[#333] text-[#EDEDED] px-8 py-4 font-bold uppercase hover:border-[#39FF14] hover:text-[#39FF14]"
            >
              <span>Continue Shopping</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12">
          <span className="label-text text-[#39FF14] text-sm tracking-[0.3em]">SECURE CHECKOUT</span>
          <h1 className="mt-2 text-4xl heading text-[#EDEDED]">Checkout</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Shipping & Payment */}
            <div className="lg:col-span-2 space-y-8">
              {/* Shipping Info */}
              <div className="bg-[#0A0A0A] border border-[#222] p-6">
                <h3 className="text-xl heading text-[#EDEDED] mb-6">Shipping Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="address" className="text-[#888] label-text">Street Address</Label>
                    <Input
                      id="address"
                      value={shipping.address}
                      onChange={(e) => setShipping(prev => ({ ...prev, address: e.target.value }))}
                      className="mt-2 gcsr-input w-full"
                      placeholder="123 Street Name"
                      required
                      data-testid="shipping-address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city" className="text-[#888] label-text">City</Label>
                    <Input
                      id="city"
                      value={shipping.city}
                      onChange={(e) => setShipping(prev => ({ ...prev, city: e.target.value }))}
                      className="mt-2 gcsr-input w-full"
                      placeholder="City"
                      required
                      data-testid="shipping-city"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="state" className="text-[#888] label-text">State</Label>
                      <Input
                        id="state"
                        value={shipping.state}
                        onChange={(e) => setShipping(prev => ({ ...prev, state: e.target.value }))}
                        className="mt-2 gcsr-input w-full"
                        placeholder="CA"
                        required
                        data-testid="shipping-state"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zip" className="text-[#888] label-text">ZIP</Label>
                      <Input
                        id="zip"
                        value={shipping.zip}
                        onChange={(e) => setShipping(prev => ({ ...prev, zip: e.target.value }))}
                        className="mt-2 gcsr-input w-full"
                        placeholder="90210"
                        required
                        data-testid="shipping-zip"
                      />
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="phone" className="text-[#888] label-text">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={shipping.phone}
                      onChange={(e) => setShipping(prev => ({ ...prev, phone: e.target.value }))}
                      className="mt-2 gcsr-input w-full"
                      placeholder="(555) 123-4567"
                      required
                      data-testid="shipping-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-[#0A0A0A] border border-[#222] p-6">
                <h3 className="text-xl heading text-[#EDEDED] mb-6">Payment Method</h3>
                <div className="space-y-4">
                  {paymentMethods.map((method) => (
                    <div
                      key={method.id}
                      onClick={() => setPaymentMethod(method.id)}
                      className={`payment-card flex items-center gap-4 ${
                        paymentMethod === method.id ? "selected" : ""
                      }`}
                      data-testid={`payment-${method.id}`}
                    >
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: method.color }}
                      >
                        {method.id === "zelle" ? "Z" : method.id === "cashapp" ? "$" : "V"}
                      </div>
                      <div className="flex-1">
                        <p className="text-[#EDEDED] font-semibold">{method.name}</p>
                        <p className="text-[#888] text-sm">{method.handle}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 ${
                        paymentMethod === method.id 
                          ? "border-[#39FF14] bg-[#39FF14]" 
                          : "border-[#333]"
                      }`}>
                        {paymentMethod === method.id && (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-2 h-2 bg-black rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-[#888] text-sm">
                  After placing your order, you'll receive payment instructions. 
                  Your order will be processed once payment is confirmed.
                </p>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-[#0A0A0A] border border-[#222] p-6 sticky top-32">
                <h3 className="text-xl heading text-[#EDEDED] mb-6">Order Summary</h3>

                {/* Items */}
                <div className="space-y-4 pb-6 border-b border-[#222]">
                  {cart.items.map((item, index) => (
                    <div key={index} className="flex gap-3">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-16 h-16 object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-[#EDEDED] text-sm truncate">{item.name}</p>
                        <p className="text-[#888] text-xs">
                          {item.size && `${item.size}`}
                          {item.size && item.color && " / "}
                          {item.color && `${item.color}`}
                        </p>
                        <p className="text-[#888] text-xs">Qty: {item.quantity}</p>
                      </div>
                      <p className="text-[#EDEDED] text-sm">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="space-y-4 py-6 border-b border-[#222]">
                  <div className="flex justify-between text-[#888]">
                    <span>Subtotal</span>
                    <span>${cart.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#888]">
                    <span>Shipping</span>
                    <span>{shippingCost === 0 ? "Free" : `$${shippingCost.toFixed(2)}`}</span>
                  </div>
                </div>

                <div className="flex justify-between py-6 text-lg">
                  <span className="text-[#EDEDED] heading">Total</span>
                  <span className="text-[#39FF14] heading" data-testid="checkout-total">
                    ${totalWithShipping.toFixed(2)}
                  </span>
                </div>

                <button
                  type="submit"
                  disabled={loading || !paymentMethod}
                  className="w-full btn-skew bg-[#39FF14] text-black px-8 py-4 font-bold uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="place-order-btn"
                >
                  <span>{loading ? "Processing..." : "Place Order"}</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
